import React from 'react';
import { View } from 'react-native-web';
import { Button, IconButton } from '../../ui';
import { TrashIcon, UndoIcon } from '../../components/icons';

/** Whether a row can be reverted to its Skyward feed state. An excluded row offers Restore instead.
 *  The `!row.excluded` clause is currently unreachable — an excluded row's `corrected` is always
 *  false, so `row.corrected` above already excludes it — but it must stay: Revert calls revertAll,
 *  which deletes every correction on the claim line, including the exclusion, so if this ever became
 *  reachable an excluded row's Revert would silently wipe out the exclusion along with everything else. */
export const canRevertToFeed = (row) => {
  if (!row) {
    return false;
  }
  return row.source === 'SKYWARD' && row.corrected && !row._removed && !row._reverted && !row.excluded;
};

/** Whether a row can have its durable exclusion undone — only a Skyward row already excluded. */
export const canRestore = (row) => {
  if (!row) {
    return false;
  }
  return row.source === 'SKYWARD' && !!row.excluded;
};

/* Edit-mode row actions. Removal is staged, not immediate, so the icon toggles to an undo.
   "Revert" stays rendered once staged, its label toggled to an undo — the same treatment as Restore —
   so staging it has a visible effect and a direct way back that isn't Discard-everything.
   An excluded row is already removed, so the trash icon (which would stage a second, redundant exclusion)
   gives way to Restore, whose own label toggles the same way the removal icon does. */
export const LossActionsCell = (props) => {
  const { data, onToggleRemoval, onToggleRevert, onToggleRestore } = props;
  if (!data) {
    return null;
  }

  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      {data.excluded ? null : (
        <IconButton
          onPress={() => onToggleRemoval(data)}
          accessibility={{ accessibilityLabel: data._removed ? 'Undo removal' : 'Remove loss' }}
          size="small"
        >
          {data._removed ? <UndoIcon /> : <TrashIcon />}
        </IconButton>
      )}
      {canRevertToFeed(data) || data._reverted ? (
        <Button variant="text" color="$primary" label={data._reverted ? 'Undo revert' : 'Revert'} onPress={() => onToggleRevert(data)} />
      ) : null}
      {canRestore(data) ? (
        <Button
          variant="text"
          color="$primary"
          label={data._restored ? 'Undo restore' : 'Restore'}
          onPress={() => typeof onToggleRestore === 'function' && onToggleRestore(data)}
        />
      ) : null}
    </View>
  );
};
